import React, { useState, useRef, useEffect } from 'react';
import { useLandingPageData } from '../../context/LandingPageContext';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import {
  MessageSquare, Sparkles, Stethoscope, HeartHandshake, Trophy,
  ClipboardCheck, Scissors, Eye, Target, Shield, Clock, Heart
} from 'lucide-react';

const iconMap = {
  MessageSquare, Sparkles, Stethoscope, HeartHandshake, Trophy,
  ClipboardCheck, Scissors, Eye, Target, Shield, Clock, Heart,
};

const stepTranslations = {
  'Kostenlose Beratung': 'Free Consultation',
  'Individuelle Planung': 'Individual Planning',
  'DHI Behandlung': 'DHI Treatment',
  'Sofort Alltagstauglich': 'Immediately Back to Daily Life',
  'Natürliches Ergebnis': 'Natural Result',
};

const descTranslations = {
  'Wir analysieren Ihre Haarsituation und besprechen, ob die DHI-Methode ohne Rasur für Sie geeignet ist.':
    'We analyze your hair situation and discuss whether the DHI method without shaving is suitable for you.',
  'Wir planen die Haarlinie und die Verteilung der Grafts, um ein natürliches Ergebnis zu erzielen.':
    'We plan the hairline and graft distribution to achieve a natural result.',
  'Mit dem Choi-Pen werden die Haarfollikel einzeln entnommen und direkt implantiert - ohne Rasur.':
    'With the Choi pen, hair follicles are individually extracted and directly implanted — without shaving.',
  'Sie können die Klinik verlassen und direkt wieder Ihrem Alltag nachgehen. Keine sichtbaren Spuren.':
    'You can leave the clinic and immediately return to your daily life. No visible traces.',
  'Nach 12-18 Monaten genießen Sie volleres Haar - ohne dass jemand weiß, dass Sie eine Transplantation hatten.':
    'After 12-18 months, enjoy fuller hair — without anyone knowing you had a transplant.',
};

const ACCENT = '#7BA7C2';

const DhiProcess = () => {
  const { processSteps, features } = useLandingPageData();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const sectionRef = useRef(null);
  const [lineProgress, setLineProgress] = useState(0);
  const [visibleSteps, setVisibleSteps] = useState(new Set());
  const stepRefs = useRef([]);

  if (!features?.showDhiProcess) return null;

  const t = (de, en) => language === 'de' ? de : en;

  const getTitle = (step) => language === 'en' && stepTranslations[step.title]
    ? stepTranslations[step.title] : step.title;
  const getDesc = (step) => language === 'en' && descTranslations[step.description]
    ? descTranslations[step.description] : step.description;

  return (
    <DhiProcessInner
      processSteps={processSteps}
      isDark={isDark}
      t={t}
      getTitle={getTitle}
      getDesc={getDesc}
      language={language}
    />
  );
};

const DhiProcessInner = ({ processSteps, isDark, t, getTitle, getDesc, language }) => {
  const sectionRef = useRef(null);
  const [lineProgress, setLineProgress] = useState(0);
  const [visibleSteps, setVisibleSteps] = useState(new Set());
  const stepRefs = useRef([]);

  // Scroll-based line fill
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const vh = window.innerHeight;
      const start = -rect.top + vh * 0.3;
      const total = rect.height - vh * 0.4;
      if (total <= 0) return;
      setLineProgress(Math.min(100, Math.max(0, (start / total) * 100)));
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // IntersectionObserver for each step
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            setVisibleSteps(prev => new Set(prev).add(Number(e.target.dataset.step)));
          }
        });
      },
      { threshold: 0.3, rootMargin: '0px 0px -10% 0px' }
    );
    stepRefs.current.forEach(el => { if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [processSteps]);

  if (!processSteps?.length) return null;

  return (
    <section
      id="ablauf"
      ref={sectionRef}
      data-testid="dhi-process-timeline"
      className={`py-16 sm:py-24 relative overflow-hidden transition-colors duration-300 ${
        isDark ? 'bg-[#0a0e17]' : 'bg-[#f0f4f8]'
      }`}
    >
      {/* Subtle background glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className={`absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full blur-[120px] ${
          isDark ? 'bg-[#7BA7C2]/4' : 'bg-[#5A8BA6]/5'
        }`} />
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Header */}
        <div className="text-center mb-14 sm:mb-20">
          <span className={`inline-block px-4 py-1.5 rounded-full text-xs sm:text-sm font-medium mb-4 ${
            isDark ? 'bg-[#7BA7C2]/10 text-[#7BA7C2] border border-[#7BA7C2]/20' : 'bg-[#5A8BA6]/10 text-[#5A8BA6] border border-[#5A8BA6]/20'
          }`}>
            {t('Ihr Weg zum Ergebnis', 'Your Path to Results')}
          </span>
          <h2 className={`text-2xl sm:text-3xl md:text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {t('Der ', 'The ')}
            <span className="bg-gradient-to-r from-[#7BA7C2] to-[#9BBFD9] bg-clip-text text-transparent">
              {t('Ablauf', 'Process')}
            </span>
          </h2>
          <p className={`max-w-xl mx-auto text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {t(
              'Von der Beratung bis zum Endergebnis — Ihr persönlicher Behandlungsweg',
              'From consultation to final result — your personal treatment journey'
            )}
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Center line (desktop) / Left line (mobile) */}
          <div
            className={`absolute top-3 bottom-4 w-[2px] left-[calc(1.375rem-1px)] lg:left-1/2 lg:-translate-x-[1px] ${
              isDark ? 'bg-white/[0.06]' : 'bg-gray-200'
            }`}
          >
            {/* Animated fill */}
            <div
              className="w-full rounded-full transition-[height] duration-100 ease-out"
              style={{
                height: `${lineProgress}%`,
                background: `linear-gradient(to bottom, ${ACCENT}, #22d3ee)`,
                boxShadow: `0 0 8px ${ACCENT}60`,
              }}
            />
          </div>

          {/* Steps */}
          <div className="relative space-y-8 sm:space-y-12 lg:space-y-16">
            {processSteps.map((step, index) => {
              const Icon = iconMap[step.icon] || MessageSquare;
              const isVisible = visibleSteps.has(step.step);
              const isLeft = index % 2 === 0;
              const stepProgress = ((index + 0.5) / processSteps.length) * 100;
              const isLineReached = lineProgress >= stepProgress;

              return (
                <div
                  key={step.step}
                  ref={el => stepRefs.current[index] = el}
                  data-step={step.step}
                  data-testid={`dhi-process-step-${step.step}`}
                  className="relative flex items-start lg:items-center"
                >
                  {/* Mobile/Tablet: icon left, content right */}
                  <div className="flex items-start gap-4 sm:gap-6 lg:hidden w-full">
                    {/* Icon circle */}
                    <div className="relative flex-shrink-0 z-10 w-11 mt-2">
                      <div
                        className="w-11 h-11 rounded-full flex items-center justify-center border-2 transition-all duration-700"
                        style={{
                          borderColor: isLineReached ? `${ACCENT}90` : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'),
                          background: isDark ? '#0d1117' : '#fff',
                          boxShadow: isLineReached ? `0 0 20px ${ACCENT}40` : 'none',
                          transform: isVisible ? 'scale(1)' : 'scale(0.7)',
                          opacity: isVisible ? 1 : 0.4,
                          transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        }}
                      >
                        <Icon
                          className="w-5 h-5 transition-colors duration-500"
                          style={{ color: isLineReached ? ACCENT : (isDark ? '#4b5563' : '#9ca3af') }}
                        />
                      </div>
                    </div>

                    {/* Content card */}
                    <div
                      className={`flex-1 rounded-xl p-4 sm:p-5 border transition-all duration-700 ${
                        isDark
                          ? 'bg-white/[0.02] border-white/[0.06]'
                          : 'bg-white border-gray-100 shadow-sm'
                      }`}
                      style={{
                        opacity: isVisible ? 1 : 0,
                        transform: isVisible ? 'translateX(0)' : 'translateX(20px)',
                        transition: 'all 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
                        transitionDelay: `${index * 0.08}s`,
                      }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full"
                          style={{
                            background: ACCENT + '15',
                            color: ACCENT,
                          }}
                        >
                          {String(step.step).padStart(2, '0')}
                        </span>
                      </div>
                      <h3 className={`text-base sm:text-lg font-semibold mb-1.5 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {getTitle(step)}
                      </h3>
                      <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {getDesc(step)}
                      </p>
                    </div>
                  </div>

                  {/* Desktop: alternating left/right */}
                  <div className="hidden lg:flex items-center w-full">
                    {/* Left content (even steps) */}
                    <div className={`flex-1 ${isLeft ? 'pr-10' : ''}`}>
                      {isLeft && (
                        <div
                          className={`rounded-xl p-6 border text-right transition-all duration-700 ${
                            isDark
                              ? 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]'
                              : 'bg-white border-gray-100 shadow-sm hover:shadow-md'
                          }`}
                          style={{
                            opacity: isVisible ? 1 : 0,
                            transform: isVisible ? 'translateX(0)' : 'translateX(-30px)',
                            transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
                            transitionDelay: `${index * 0.1}s`,
                          }}
                        >
                          <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full inline-block mb-3"
                            style={{ background: ACCENT + '15', color: ACCENT }}>
                            {String(step.step).padStart(2, '0')}
                          </span>
                          <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {getTitle(step)}
                          </h3>
                          <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {getDesc(step)}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Center icon */}
                    <div className="relative z-10 flex-shrink-0 mt-2">
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center border-2 transition-all duration-700"
                        style={{
                          borderColor: isLineReached ? `${ACCENT}90` : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'),
                          background: isDark ? '#0d1117' : '#fff',
                          boxShadow: isLineReached ? `0 0 24px ${ACCENT}50` : 'none',
                          transform: isVisible ? 'scale(1)' : 'scale(0.6)',
                          opacity: isVisible ? 1 : 0.3,
                          transition: 'all 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
                          transitionDelay: `${index * 0.1}s`,
                        }}
                      >
                        <Icon
                          className="w-6 h-6 transition-colors duration-500"
                          style={{ color: isLineReached ? ACCENT : (isDark ? '#4b5563' : '#9ca3af') }}
                        />
                      </div>
                    </div>

                    {/* Right content (odd steps) */}
                    <div className={`flex-1 ${!isLeft ? 'pl-10' : ''}`}>
                      {!isLeft && (
                        <div
                          className={`rounded-xl p-6 border transition-all duration-700 ${
                            isDark
                              ? 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]'
                              : 'bg-white border-gray-100 shadow-sm hover:shadow-md'
                          }`}
                          style={{
                            opacity: isVisible ? 1 : 0,
                            transform: isVisible ? 'translateX(0)' : 'translateX(30px)',
                            transition: 'all 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
                            transitionDelay: `${index * 0.1}s`,
                          }}
                        >
                          <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full inline-block mb-3"
                            style={{ background: ACCENT + '15', color: ACCENT }}>
                            {String(step.step).padStart(2, '0')}
                          </span>
                          <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {getTitle(step)}
                          </h3>
                          <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {getDesc(step)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DhiProcess;
